import {FailedLinkStatus, OtpInfo} from "@/src/components/OrderAuthModal/model";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {getProcessInstance} from "@/src/api/services/processInstance";
import {isNil} from "lodash";
import {FAILED_LINK_STATUSES} from "@/src/components/OrderAuthModal/constants";
import {ApiCallsTypes} from "@/src/components/pages/StepPage/components/apiCalls/contants";
import {useEffect, useState} from "react";
import {useContextStore} from "@/src/stores/useContextStore";
import {parseContextVariableValue} from "@/src/shared/utils/dataTypeHelpers";
import {ContextInstanceVariableType} from "@/src/shared/schemas";
import {DataTypeEnum} from "@/src/shared/models/contextInstance";
import {useRouter, useSearchParams} from "next/navigation";
import {runApiCall} from "@/src/api/services/action";
import {PROCESS_INSTANCE_QUERY, PRODUCT_INSTANCE_QUERY} from "@/src/shared/const";
import {useForm} from "react-hook-form";
import {useProcessSecurity} from "@/src/hooks/useProcessSecurity";



interface useOrderAuthEfProps {
    processInstanceId: FailedLinkStatus | string | null;
    productSlug: string | null;
}

export const useOrderAuthEf = ({ processInstanceId, productSlug }: useOrderAuthEfProps) => {

    const queryClient = useQueryClient();
    const router = useRouter();
    const searchParams = useSearchParams();
    const getVariable = useContextStore((state) => state.getVariable);
    const { createSession } = useProcessSecurity(processInstanceId as string);

    const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
    const [otpInfo, setOtpInfo] = useState<OtpInfo | null>(null);
    const [formError, setFormError] = useState<string | null>(null);

    const otpForm = useForm({
        defaultValues: { controlCode: otpInfo?.controlCode, phoneNumberCode: "" },
    });

    const isValidProcessId = !FAILED_LINK_STATUSES.includes(processInstanceId as FailedLinkStatus);

    const { data: processInstance } = useQuery({
        queryKey: ["processInstance", processInstanceId],
        queryFn: () => getProcessInstance({ processInstanceId: `${processInstanceId}` }),
        enabled: !!processInstanceId && !isNil(processInstanceId) && isValidProcessId,
    });

    const { mutateAsync: fetchSendEmailCode, isPending: isLoadingEmailCode } = useMutation({
        mutationFn: runApiCall,
        onSuccess: (response, variables) => {
            queryClient.invalidateQueries({
                queryKey: ["processInstance", processInstanceId],
            });

            if (response?.identificationCodeId) {
                setOtpInfo({ identificationCodeId: response.identificationCodeId, controlCode: response?.controlCode })
            } else {
                const apiCallError = response?.errors[0]?.errorDescription;

                apiCallError && setFormError(apiCallError);
            }

            setShowConfirmModal(false);
        },
        onError: () => {
            setShowConfirmModal(false);
            setFormError("Повторіть спробу пізніше.");
        }
    })

    const { mutateAsync: fetchVerifyCode, isPending: isLoadingVerifyCode } = useMutation({
        mutationFn: runApiCall,
        onSuccess: (response) => {
            if (response.isCodeVerified) {
                createSession(processInstanceId as string);
                router.push(getProductLink());
                setOtpInfo(null);
            }

            const apiCallError = response?.errors[0]?.errorDescription;

            apiCallError && otpForm.setError("phoneNumberCode", {
                message: apiCallError,
            });

        },
        onError: () => {
            setFormError("Повторіть спробу пізніше.");
        }
    })

    const prepareContextVariables = (newVariables: Record<string, string>): ContextInstanceVariableType[] => {
         return Object.keys(newVariables).map((key) => {
                const contextVariableToUpdate = getVariable(key);

                if (contextVariableToUpdate) {
                    const parsedValue = parseContextVariableValue(newVariables[key], {
                        dataType: contextVariableToUpdate.dataType,
                        key,
                        isArray: contextVariableToUpdate.isArray,
                    });

                    return { ...contextVariableToUpdate, value: parsedValue };
                } else {
                    const parsedValue = parseContextVariableValue(newVariables[key], {
                        dataType: DataTypeEnum.STRING,
                        key,
                        isArray: false,
                    });

                    return { dataType: DataTypeEnum.STRING, isArray: false, readOnly: false, jsonSchema: null, key, value: parsedValue }
                }
            }) as ContextInstanceVariableType[]
    }

    const sendEmailCode = async () => {
        processInstance && fetchSendEmailCode({
            actionName: ApiCallsTypes.SEND_EMAIL_IDENTIFICATION_CODE,
            body: {
                id: processInstance.id,
                processId: processInstance.processId,
                activeStepId: processInstance.activeStepId,
                contextInstance: processInstance.contextInstance,
            }
        })
    }

    const resendEmailCode = () => {
        otpForm.setValue('phoneNumberCode', '');
        sendEmailCode();
    }
    const verifyOtpCode = async (codeToVerify: string) => {
        const variables = prepareContextVariables({
            identificationCodeId: otpInfo?.identificationCodeId ?? "",
            codeToVerify
        })

        processInstance && otpInfo && fetchVerifyCode({
            actionName: ApiCallsTypes.VERIFY_EMAIL_IDENTIFICATION_CODE,
            body: {
                id: processInstance.id,
                processId: processInstance.processId,
                activeStepId: processInstance.activeStepId,
                contextInstance: {
                    id: processInstance.contextInstance.id,
                    variables
                },
            }
        })
    }

    const getProductLink = (): string => {
        return `/products/${productSlug}?p=${processInstanceId}`
    }

    const getEmail = (): string => {
        const emailVariableValue = processInstance?.contextInstance?.variables?.find(item => item.key === "email")?.value;
        return typeof emailVariableValue == "string" ? emailVariableValue : "";
    }

    const clearSearchParams = () => {
        const params = new URLSearchParams(searchParams.toString());
        params?.delete(PROCESS_INSTANCE_QUERY);
        params?.delete(PRODUCT_INSTANCE_QUERY);

        router.replace( `/?${params.toString()}`);
    }

    useEffect(() => {
        processInstance && setShowConfirmModal(true);
    }, [processInstance]);

    return {
        otpForm,
        otpInfo,
        formError,
        resendEmailCode,
        handleCloseErrorModal: () => {
            clearSearchParams();
            setFormError(null);
        },
        handleCloseOtpModal: () => {
            clearSearchParams();
            setOtpInfo(null);
        },
        showConfirmModal,
        handleCloseConfirmModal: () => {
            clearSearchParams();
            setShowConfirmModal(false);
        },
        email: getEmail(),
        sendEmailCode,
        verifyOtpCode,
        isLoadingEmailCode,
        isLoadingVerifyCode
    }
}
